import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, Container, Paper } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { ShieldCheck } from "lucide-react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { loginUserByApple } from "../../reducers/authentication.slice";
import { migratePayout } from "../../reducers/payouts.slice";
import CustomLoader from "../../components/CustomLoader";
import { AuthContext } from "../../context/authContext";

export const VerificationPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { showToast } = useContext(AuthContext);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = params.get("auth_token");

    if (accessToken) {
      dispatch(loginUserByApple({ authToken: accessToken })).then((res) => {
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          showToast(res?.error?.message, "error");
          return;
        }
        if (res?.payload?.requires_2fa_action) {
          setShowLoader(false);
          navigate(res.payload.requires_2fa_action === "setup" ? "/setup-2fa" : "/login", {
            state: { userData: res.payload },
          });
          return;
        }
        localStorage.setItem("user", JSON.stringify(res?.payload));
        if (res.payload?.status === "SUSPENDED") {
          setShowLoader(false);
          showToast(
            "Your account is suspended. You do not have access to log in.",
            "error"
          );
          return;
        }

        /* CHECKING FOR WHETHER THE USER IS VERIFIED OR NOT */
        if (!res?.payload?.is_verified) {
          setShowLoader(false);
          sessionStorage.setItem("activeStep", 3);
          navigate("/signup", {
            state: {
              isVerifiedCheck: true,
              email: res?.payload?.email,
            },
          });
          return;
        }

        if (res?.payload?.is_discord_verified) {
          dispatch(migratePayout()).then((mig_response) => {
            if (
              mig_response?.error &&
              Object.keys(mig_response?.error)?.length > 0
            ) {
              setShowLoader(false);
              showToast(mig_response?.error?.message, "error");
            }
          });
        }

        setShowLoader(false);
        if (res?.payload?.is_verified) {
          if (res?.payload?.role === "MEMBER") {
            navigate("/user-dashboard");
          } else if (res?.payload?.role === "MANAGER") {
            navigate("/manager-dashboard");
          } else if (res?.payload?.role === "SUPERADMIN") {
            navigate("/admin-dashboard");
          } else {
            showToast("Invalid User", "error");
          }
        }
      });
    }
  }, [location]);

  return (
    <>
      <CustomLoader open={showLoader} />
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(180deg, rgba(25, 118, 210, 0.05) 0%, rgba(0, 0, 0, 0.1) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: 3,
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(30, 30, 30, 0.8)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ShieldCheck size={48} />
            </Box>

            <Typography variant="h5" component="h1" fontWeight="500">
              Verifying User
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={40} />
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight="500">
                Please don't close this page
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                We're verifying your information. This will only take a moment.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};
